import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Trash, FloppyDisk, Power, Image as ImageIcon, Clock, UploadSimple, Spinner,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";

const DEFAULT_HERO =
    "https://images.pexels.com/photos/30541218/pexels-photo-30541218.jpeg";

const PRESET_HEROS = [
    "https://images.pexels.com/photos/30541218/pexels-photo-30541218.jpeg",
    "https://images.unsplash.com/photo-1746587021271-8de34530e438?crop=entropy&cs=srgb&fm=jpg&w=1600",
    "https://images.pexels.com/photos/15337739/pexels-photo-15337739.jpeg",
    "https://images.pexels.com/photos/19202829/pexels-photo-19202829.jpeg",
    "https://images.pexels.com/photos/28992999/pexels-photo-28992999.jpeg",
    "https://images.unsplash.com/photo-1619926096619-5956ab4dfb1b?crop=entropy&cs=srgb&fm=jpg&w=1600",
];

const DEFAULT_GALLERY = [
    "https://images.unsplash.com/photo-1619926096619-5956ab4dfb1b?crop=entropy&cs=srgb&fm=jpg&w=900",
    "https://images.pexels.com/photos/37025257/pexels-photo-37025257.jpeg",
    "https://images.pexels.com/photos/28992999/pexels-photo-28992999.jpeg",
    "https://images.unsplash.com/photo-1539252554453-80ab65ce3586?crop=entropy&cs=srgb&fm=jpg&w=900",
    "https://images.pexels.com/photos/19202829/pexels-photo-19202829.jpeg",
    "https://images.unsplash.com/photo-1624128082323-beb6b8b508db?crop=entropy&cs=srgb&fm=jpg&w=900",
    "https://images.pexels.com/photos/15337739/pexels-photo-15337739.jpeg",
];

const DEFAULT_ABOUT_IMAGE =
    "https://images.pexels.com/photos/15337739/pexels-photo-15337739.jpeg";

/**
 * One editable image cell. Click or drag-and-drop a file to replace it.
 * Falls back to a default if the slot is empty.
 *  - onChange(url): receives the new URL string when upload completes
 *  - onSelect(): optional, fires when the tile is clicked while not editing
 */
function EditableImageSlot({
    url,
    fallback,
    onChange,
    onSelect,
    onUpload,
    label,
    selected = false,
    aspect = "aspect-video",
    testId,
}) {
    const inputRef = useRef(null);
    const [over, setOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const display = url || fallback;

    const handleFile = async (file) => {
        if (!file) return;
        setBusy(true);
        const newUrl = await onUpload(file);
        setBusy(false);
        if (newUrl) onChange(newUrl);
    };

    return (
        <div
            data-testid={testId}
            className={`group relative ${aspect} rounded-lg overflow-hidden border-2 transition ${
                selected
                    ? "border-primary ring-2 ring-primary/30"
                    : over
                        ? "border-primary bg-primary/5"
                        : "border-transparent hover:border-border"
            }`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const f = e.dataTransfer?.files?.[0];
                if (f) handleFile(f);
            }}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                    e.target.value = "";
                }}
                className="hidden"
            />
            {display ? (
                <img
                    src={display}
                    alt=""
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={onSelect}
                />
            ) : (
                <div className="w-full h-full grid place-items-center bg-muted text-muted-foreground text-xs">
                    {label}
                </div>
            )}
            {/* Hover overlay with replace + clear actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors flex items-end justify-end gap-1 p-1.5">
                {busy ? (
                    <span className="text-white text-[10px] font-bold uppercase tracking-widest bg-black/60 px-2 py-1 rounded-full">
                        Subiendo…
                    </span>
                ) : (
                    <>
                        <button
                            type="button"
                            data-testid={`${testId}-replace`}
                            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                            className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-white text-foreground px-2 py-1 rounded-full hover:bg-primary hover:text-primary-foreground transition"
                        >
                            <UploadSimple size={11} weight="bold" /> Cambiar
                        </button>
                        {url && (
                            <button
                                type="button"
                                data-testid={`${testId}-clear`}
                                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                                className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center h-6 w-6 rounded-full bg-white text-destructive hover:bg-destructive hover:text-white transition"
                                aria-label="Volver al original"
                            >
                                <Trash size={11} weight="bold" />
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default function Settings() {
    const [form, setForm] = useState({
        is_open: true,
        delivery_enabled: true,
        hours: [],
        hero_image_url: DEFAULT_HERO,
        hero_overlay_opacity: 0.55,
        hero_presets: [],
        gallery_images: [],
        about_image_url: "",
        about: {
            title_en: "",
            title_es: "",
            body_en: "",
            body_es: "",
            quote_en: "",
            quote_es: "",
            quote_author: "",
            stats: [],
        },
        discount_popup: {
            enabled: true,
            delay_seconds: 8,
            kicker_en: "Limited time",
            kicker_es: "Tiempo limitado",
            title_en: "",
            title_es: "",
            body_en: "",
            body_es: "",
            cta_en: "",
            cta_es: "",
        },
        banner: {
            enabled: true,
            text_en: "",
            text_es: "",
            speed_seconds: 30,
        },
    });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        api.get("/settings")
            .then((r) => setForm((prev) => ({
                ...prev,
                ...r.data,
                about: { ...prev.about, ...(r.data.about || {}) },
                discount_popup: { ...prev.discount_popup, ...(r.data.discount_popup || {}) },
                banner: { ...prev.banner, ...(r.data.banner || {}) },
            })))
            .catch(() => toast.error("Couldn't load settings"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setHourField = (idx, key, value) => {
        const hours = [...form.hours];
        hours[idx] = { ...hours[idx], [key]: value };
        setForm({ ...form, hours });
    };

    const addRow = () =>
        setForm({
            ...form,
            hours: [...form.hours, { days: "", open: "", close: "" }],
        });

    const removeRow = (idx) =>
        setForm({
            ...form,
            hours: form.hours.filter((_, i) => i !== idx),
        });

    const save = async () => {
        const cleanHours = form.hours.filter(
            (h) => h.days && h.open && h.close,
        );
        if (cleanHours.length === 0) {
            toast.error("Add at least one schedule row");
            return;
        }
        if (!form.hero_image_url) {
            toast.error("Hero image URL is required");
            return;
        }
        setBusy(true);
        try {
            await api.put(
                "/admin/settings",
                {
                    is_open: !!form.is_open,
                    delivery_enabled: !!form.delivery_enabled,
                    hours: cleanHours,
                    hero_image_url: form.hero_image_url.trim(),
                    hero_overlay_opacity: Number(form.hero_overlay_opacity),
                    hero_presets: (form.hero_presets || []).filter(Boolean),
                    gallery_images: (form.gallery_images || []).filter(Boolean),
                    about_image_url: (form.about_image_url || "").trim(),
                    about: {
                        title_en: (form.about?.title_en || "").trim(),
                        title_es: (form.about?.title_es || "").trim(),
                        body_en: (form.about?.body_en || "").trim(),
                        body_es: (form.about?.body_es || "").trim(),
                        quote_en: (form.about?.quote_en || "").trim(),
                        quote_es: (form.about?.quote_es || "").trim(),
                        quote_author: (form.about?.quote_author || "").trim(),
                        stats: (form.about?.stats || [])
                            .filter((s) => s && (s.value || s.label))
                            .map((s) => ({
                                value: (s.value || "").trim().slice(0, 20),
                                label: (s.label || "").trim().slice(0, 60),
                            })),
                    },
                    discount_popup: {
                        enabled: !!form.discount_popup?.enabled,
                        delay_seconds: Number(form.discount_popup?.delay_seconds) || 8,
                        kicker_en: (form.discount_popup?.kicker_en || "").trim().slice(0, 40),
                        kicker_es: (form.discount_popup?.kicker_es || "").trim().slice(0, 40),
                        title_en: (form.discount_popup?.title_en || "").trim(),
                        title_es: (form.discount_popup?.title_es || "").trim(),
                        body_en: (form.discount_popup?.body_en || "").trim(),
                        body_es: (form.discount_popup?.body_es || "").trim(),
                        cta_en: (form.discount_popup?.cta_en || "").trim(),
                        cta_es: (form.discount_popup?.cta_es || "").trim(),
                    },
                    banner: {
                        enabled: !!form.banner?.enabled,
                        text_en: (form.banner?.text_en || "").trim(),
                        text_es: (form.banner?.text_es || "").trim(),
                        speed_seconds: Number(form.banner?.speed_seconds) || 30,
                    },
                },
                { headers: authHeaders() },
            );
            toast.success("Saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    // Generic image-slot helpers used by Gallery and Quick Presets sections.
    const uploadToUrl = async (file, purpose = "image") => {
        if (!file || !file.type.startsWith("image/")) {
            toast.error("That's not an image file");
            return null;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error("Max 25 MB. Use a smaller image.");
            return null;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", purpose);
        try {
            const r = await api.post("/admin/upload/image", fd, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            return r.data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
            return null;
        }
    };

    const updateSlot = (key, index, value, fill = "") => {
        // Pad the array so we can write to index `index` without losing siblings.
        const next = [...(form[key] || [])];
        while (next.length <= index) next.push(fill);
        next[index] = value;
        setForm((f) => ({ ...f, [key]: next }));
    };

    const uploadFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("That's not an image file");
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error("Max 25 MB. Use a smaller image.");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", "hero");
        setUploading(true);
        try {
            const r = await api.post("/admin/upload/image", fd, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, hero_image_url: r.data.url }));
            toast.success(
                `Uploaded · ${r.data.width}×${r.data.height} · ${Math.round(r.data.size_kb)} KB`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) uploadFile(f);
    };

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (f) uploadFile(f);
        e.target.value = ""; // reset so the same file can be picked again
    };

    if (loading) {
        return (
            <div data-testid="admin-settings" className="text-muted-foreground">
                Loading…
            </div>
        );
    }

    return (
        <div data-testid="admin-settings">
            <PageHeader
                kicker="Configuración"
                title="Site settings"
                action={
                    <Button
                        data-testid="settings-save"
                        onClick={save}
                        disabled={busy}
                        className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                    >
                        <FloppyDisk size={16} weight="fill" className="mr-2" />
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                }
            />

            {/* Open / Closed */}
            <Card
                className={`p-6 mb-6 transition-colors ${
                    form.is_open
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-red-50 border-red-200"
                }`}
            >
                <div className="flex items-start gap-4">
                    <span
                        className={`grid place-items-center w-12 h-12 rounded-full ${
                            form.is_open
                                ? "bg-emerald-200 text-emerald-900"
                                : "bg-red-200 text-red-900"
                        }`}
                    >
                        <Power size={22} weight="fill" />
                    </span>
                    <div className="flex-1">
                        <p className="font-display font-black text-xl tracking-tight">
                            Estado del negocio
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                            Activa el modo cerrado para deshabilitar pedidos online en todo el sitio.
                        </p>
                        <div className="mt-3 inline-flex items-center gap-2">
                            <span
                                className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                                    form.is_open
                                        ? "bg-emerald-500 pulse-dot text-emerald-500"
                                        : "bg-red-500"
                                }`}
                            />
                            <span
                                className={`text-sm font-bold uppercase tracking-widest ${
                                    form.is_open ? "text-emerald-900" : "text-red-900"
                                }`}
                            >
                                {form.is_open
                                    ? "Negocio ABIERTO — los pedidos están activos"
                                    : "Negocio CERRADO — no se aceptan pedidos"}
                            </span>
                        </div>
                    </div>
                    <Switch
                        data-testid="settings-open-toggle"
                        checked={!!form.is_open}
                        onCheckedChange={(v) => setForm({ ...form, is_open: v })}
                        className="scale-125 data-[state=checked]:bg-emerald-500"
                    />
                </div>
            </Card>

            {/* Delivery on / off */}
            <Card
                className={`p-6 mb-6 transition-colors ${
                    form.delivery_enabled
                        ? "bg-blue-50 border-blue-200"
                        : "bg-amber-50 border-amber-200"
                }`}
            >
                <div className="flex items-start gap-4">
                    <span
                        className={`grid place-items-center w-12 h-12 rounded-full ${
                            form.delivery_enabled
                                ? "bg-blue-200 text-blue-900"
                                : "bg-amber-200 text-amber-900"
                        }`}
                    >
                        <Power size={22} weight="fill" />
                    </span>
                    <div className="flex-1">
                        <p className="font-display font-black text-xl tracking-tight">
                            Delivery a domicilio
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                            Apaga el delivery cuando no tengas repartidores disponibles. Los clientes solo podrán pedir Pickup.
                        </p>
                        <div className="mt-3 inline-flex items-center gap-2">
                            <span
                                className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                                    form.delivery_enabled ? "bg-blue-500" : "bg-amber-500"
                                }`}
                            />
                            <span
                                className={`text-sm font-bold uppercase tracking-widest ${
                                    form.delivery_enabled ? "text-blue-900" : "text-amber-900"
                                }`}
                            >
                                {form.delivery_enabled
                                    ? "Delivery ACTIVO"
                                    : "Delivery PAUSADO — solo pickup"}
                            </span>
                        </div>
                    </div>
                    <Switch
                        data-testid="settings-delivery-toggle"
                        checked={!!form.delivery_enabled}
                        onCheckedChange={(v) => setForm({ ...form, delivery_enabled: v })}
                        className="scale-125 data-[state=checked]:bg-blue-500"
                    />
                </div>
            </Card>

            {/* Hours */}
            <Card className="p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                        <Clock size={20} weight="fill" />
                    </span>
                    <div>
                        <h2 className="font-display font-black text-xl tracking-tight">
                            Horarios de apertura
                        </h2>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Mostrados en el footer y la sección Find us
                        </p>
                    </div>
                </div>

                <div className="space-y-3" data-testid="hours-list">
                    {form.hours.map((row, idx) => (
                        <div
                            key={idx}
                            data-testid={`hours-row-${idx}`}
                            className="grid grid-cols-1 md:grid-cols-[1.4fr,1fr,1fr,auto] gap-2 md:gap-3 items-end"
                        >
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Día(s)
                                </Label>
                                <Input
                                    data-testid={`hours-days-${idx}`}
                                    placeholder="Mon - Sat"
                                    value={row.days}
                                    onChange={(e) => setHourField(idx, "days", e.target.value)}
                                />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Apertura
                                </Label>
                                <Input
                                    data-testid={`hours-open-${idx}`}
                                    placeholder="11:00 AM"
                                    value={row.open}
                                    onChange={(e) => setHourField(idx, "open", e.target.value)}
                                />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Cierre
                                </Label>
                                <Input
                                    data-testid={`hours-close-${idx}`}
                                    placeholder="8:00 PM"
                                    value={row.close}
                                    onChange={(e) => setHourField(idx, "close", e.target.value)}
                                />
                            </div>
                            <button
                                data-testid={`hours-remove-${idx}`}
                                onClick={() => removeRow(idx)}
                                className="h-10 w-10 grid place-items-center rounded-full hover:bg-red-50 text-destructive"
                                aria-label="Remove row"
                            >
                                <Trash size={16} weight="bold" />
                            </button>
                        </div>
                    ))}
                </div>

                <Button
                    variant="outline"
                    onClick={addRow}
                    data-testid="hours-add"
                    className="rounded-full mt-4"
                >
                    <Plus size={16} weight="bold" className="mr-1.5" /> Add schedule
                </Button>

                <p className="mt-4 text-xs text-muted-foreground">
                    💡 Usa formato como <strong>11:00 AM</strong> / <strong>8:00 PM</strong>. Si cierras manualmente, no se aceptan pedidos hasta reactivar.
                </p>
            </Card>

            {/* Hero image */}
            <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                        <ImageIcon size={20} weight="fill" />
                    </span>
                    <div>
                        <h2 className="font-display font-black text-xl tracking-tight">
                            Foto de portada principal
                        </h2>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Imagen de fondo del Hero · landing principal
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[1fr,1fr] gap-6">
                    <div className="space-y-3">
                        {/* Drag and drop uploader */}
                        <div
                            data-testid="hero-dropzone"
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition ${
                                dragOver
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/60 hover:bg-muted/40"
                            }`}
                        >
                            <input
                                ref={fileInputRef}
                                data-testid="hero-file-input"
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={onPickFile}
                                className="hidden"
                            />
                            {uploading ? (
                                <>
                                    <Spinner size={28} className="mx-auto text-primary animate-spin" weight="bold" />
                                    <p className="mt-3 text-sm font-bold uppercase tracking-widest text-primary">
                                        Uploading & optimizing…
                                    </p>
                                </>
                            ) : (
                                <>
                                    <UploadSimple
                                        size={28}
                                        weight="bold"
                                        className="mx-auto text-foreground/60"
                                    />
                                    <p className="mt-3 text-sm font-bold tracking-tight">
                                        Drop image here or click to upload
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        JPG / PNG / WebP · auto-resized to 1920px · max 25 MB
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <span className="h-px bg-border flex-1" />
                            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                or paste a URL
                            </span>
                            <span className="h-px bg-border flex-1" />
                        </div>

                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            URL de la imagen
                        </Label>
                        <Input
                            data-testid="hero-url"
                            placeholder="https://..."
                            value={form.hero_image_url}
                            onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                        />

                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground pt-3 block">
                            Oscurecer overlay ({Math.round(form.hero_overlay_opacity * 100)}%)
                        </Label>
                        <input
                            data-testid="hero-overlay"
                            type="range"
                            min="0"
                            max="0.95"
                            step="0.05"
                            value={form.hero_overlay_opacity}
                            onChange={(e) =>
                                setForm({ ...form, hero_overlay_opacity: parseFloat(e.target.value) })
                            }
                            className="w-full accent-primary"
                        />

                        <p className="text-xs uppercase tracking-widest text-muted-foreground pt-2">
                            Presets rápidos · clic para usar · pasa el cursor para cambiar
                        </p>
                        <div
                            className="grid grid-cols-3 gap-2"
                            data-testid="hero-presets"
                        >
                            {PRESET_HEROS.map((defaultUrl, i) => {
                                const slot = form.hero_presets?.[i] || "";
                                const effective = slot || defaultUrl;
                                return (
                                    <EditableImageSlot
                                        key={i}
                                        testId={`hero-preset-${i}`}
                                        url={slot}
                                        fallback={defaultUrl}
                                        label={`Preset ${i + 1}`}
                                        selected={form.hero_image_url === effective}
                                        onChange={(newUrl) =>
                                            updateSlot("hero_presets", i, newUrl, "")
                                        }
                                        onSelect={() =>
                                            setForm((f) => ({ ...f, hero_image_url: effective }))
                                        }
                                        onUpload={(file) => uploadToUrl(file, "preset")}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Live preview */}
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                            Live preview
                        </p>
                        <div
                            data-testid="hero-preview"
                            className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-border bg-zinc-900"
                        >
                            {form.hero_image_url && (
                                <img
                                    src={form.hero_image_url}
                                    alt="Hero preview"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            )}
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: `linear-gradient(to bottom, rgba(0,0,0,${form.hero_overlay_opacity * 0.9}), rgba(0,0,0,${form.hero_overlay_opacity}))`,
                                }}
                            />
                            <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-secondary">
                                    Dominican American Deli
                                </p>
                                <p className="font-display font-black text-2xl leading-tight mt-1">
                                    LA ESQUINA
                                    <span className="block text-primary">DEL SABOR</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* ---------------- ABOUT IMAGE (section "From abuela's kitchen") ---------------- */}
            <Card className="p-6 mt-6">
                <div className="flex items-start gap-3 mb-4">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                        <ImageIcon size={20} weight="fill" />
                    </span>
                    <div>
                        <h3 className="font-display font-black text-xl">Sobre nosotros</h3>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Imagen junto a "Family-run since day one"
                        </p>
                    </div>
                </div>

                <p className="text-[11px] text-muted-foreground mb-4">
                    Pasa el cursor sobre la imagen y haz clic en
                    <span className="font-bold"> "Cambiar"</span> o arrastra una foto.
                    Formato vertical (4:5) recomendado.
                </p>

                <div className="max-w-xs">
                    <EditableImageSlot
                        testId="about-image-slot"
                        url={form.about_image_url || ""}
                        fallback={DEFAULT_ABOUT_IMAGE}
                        label="Foto About"
                        aspect="aspect-[4/5]"
                        onChange={(newUrl) =>
                            setForm((f) => ({ ...f, about_image_url: newUrl }))
                        }
                        onUpload={(file) => uploadToUrl(file, "about")}
                    />
                </div>
            </Card>

            {/* ---------------- ABOUT TEXT (title, story, quote, stats) ---------------- */}
            <Card className="p-6 mt-6">
                <div className="flex items-start gap-3 mb-4">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary font-display font-black">
                        Aa
                    </span>
                    <div>
                        <h3 className="font-display font-black text-xl">Texto de "Nosotros"</h3>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Título, historia, cita y estadísticas · EN / ES
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    {/* Título */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Título (English)
                        </Label>
                        <Input
                            data-testid="about-title-en"
                            placeholder="Family-run since day one"
                            value={form.about?.title_en || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, title_en: e.target.value },
                                }))
                            }
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Título (Español)
                        </Label>
                        <Input
                            data-testid="about-title-es"
                            placeholder="Familiar desde el día uno"
                            value={form.about?.title_es || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, title_es: e.target.value },
                                }))
                            }
                        />
                    </div>

                    {/* Historia */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Historia (English)
                        </Label>
                        <textarea
                            data-testid="about-body-en"
                            rows={5}
                            placeholder="J & W Deli is a family-owned…"
                            value={form.about?.body_en || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, body_en: e.target.value },
                                }))
                            }
                            className="w-full rounded-md border border-input bg-background text-sm px-3 py-2 leading-relaxed"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Historia (Español)
                        </Label>
                        <textarea
                            data-testid="about-body-es"
                            rows={5}
                            placeholder="J & W Deli es un delicatessen familiar…"
                            value={form.about?.body_es || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, body_es: e.target.value },
                                }))
                            }
                            className="w-full rounded-md border border-input bg-background text-sm px-3 py-2 leading-relaxed"
                        />
                    </div>

                    {/* Cita */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Cita destacada (English)
                        </Label>
                        <Input
                            data-testid="about-quote-en"
                            placeholder={`"From abuela's kitchen to Foxon Blvd."`}
                            value={form.about?.quote_en || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, quote_en: e.target.value },
                                }))
                            }
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Cita destacada (Español)
                        </Label>
                        <Input
                            data-testid="about-quote-es"
                            placeholder='"De la cocina de la abuela a Foxon Blvd."'
                            value={form.about?.quote_es || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, quote_es: e.target.value },
                                }))
                            }
                        />
                    </div>

                    <div className="md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                            Firma de la cita
                        </Label>
                        <Input
                            data-testid="about-quote-author"
                            placeholder="The J&W Family"
                            value={form.about?.quote_author || ""}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    about: { ...f.about, quote_author: e.target.value },
                                }))
                            }
                        />
                    </div>
                </div>

                {/* Estadísticas */}
                <div className="mt-8">
                    <div className="flex items-center justify-between mb-3">
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Estadísticas (hasta 4)
                        </Label>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="about-stat-add"
                            onClick={() => {
                                const stats = [...(form.about?.stats || [])];
                                if (stats.length >= 4) return;
                                stats.push({ value: "", label: "" });
                                setForm((f) => ({ ...f, about: { ...f.about, stats } }));
                            }}
                            className="rounded-full h-8"
                            disabled={(form.about?.stats || []).length >= 4}
                        >
                            <Plus size={12} weight="bold" className="mr-1" /> Agregar
                        </Button>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {(form.about?.stats || []).map((s, i) => (
                            <div
                                key={i}
                                data-testid={`about-stat-${i}`}
                                className="relative rounded-xl border border-border bg-background p-3 pr-8"
                            >
                                <Input
                                    data-testid={`about-stat-${i}-value`}
                                    placeholder="2018"
                                    value={s.value || ""}
                                    maxLength={20}
                                    onChange={(e) => {
                                        const stats = [...(form.about?.stats || [])];
                                        stats[i] = { ...stats[i], value: e.target.value };
                                        setForm((f) => ({ ...f, about: { ...f.about, stats } }));
                                    }}
                                    className="font-display font-black text-2xl border-0 px-0 h-auto focus-visible:ring-0 shadow-none"
                                />
                                <Input
                                    data-testid={`about-stat-${i}-label`}
                                    placeholder="Founded"
                                    value={s.label || ""}
                                    maxLength={60}
                                    onChange={(e) => {
                                        const stats = [...(form.about?.stats || [])];
                                        stats[i] = { ...stats[i], label: e.target.value };
                                        setForm((f) => ({ ...f, about: { ...f.about, stats } }));
                                    }}
                                    className="text-xs uppercase tracking-widest text-muted-foreground border-0 px-0 h-7 mt-1 focus-visible:ring-0 shadow-none"
                                />
                                <button
                                    type="button"
                                    data-testid={`about-stat-${i}-remove`}
                                    onClick={() => {
                                        const stats = (form.about?.stats || []).filter((_, idx) => idx !== i);
                                        setForm((f) => ({ ...f, about: { ...f.about, stats } }));
                                    }}
                                    className="absolute top-2 right-2 h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive"
                                    aria-label="Eliminar"
                                >
                                    <Trash size={11} weight="bold" />
                                </button>
                            </div>
                        ))}
                    </div>
                    {(form.about?.stats || []).length === 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                            Si lo dejas vacío, se mostrarán las estadísticas por defecto: 2018 Founded · 100k+ Plates served · 100% Family-run
                        </p>
                    )}
                </div>
            </Card>

            {/* ---------------- GALLERY (public site "From the kitchen" tiles) ---------------- */}
            <Card className="p-6 mt-6">
                <div className="flex items-start gap-3 mb-4">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-secondary/20 text-secondary">
                        <ImageIcon size={20} weight="fill" />
                    </span>
                    <div>
                        <h3 className="font-display font-black text-xl">Galería pública</h3>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Sección "From the kitchen" · 7 fotos en mosaico
                        </p>
                    </div>
                </div>

                <p className="text-[11px] text-muted-foreground mb-4">
                    Arrastra una imagen sobre cada casilla o haz clic en
                    <span className="font-bold"> "Cambiar"</span> para reemplazarla.
                    Las posiciones 1, 4 y 7 son más grandes en la web.
                </p>

                <div
                    data-testid="gallery-editor"
                    className="grid grid-cols-2 md:grid-cols-4 auto-rows-[110px] md:auto-rows-[130px] gap-2"
                >
                    {DEFAULT_GALLERY.map((defaultUrl, i) => {
                        const slot = form.gallery_images?.[i] || "";
                        const span =
                            i === 0 ? "row-span-2"
                            : i === 3 ? "col-span-2"
                            : i === 6 ? "col-span-2"
                            : "";
                        return (
                            <div key={i} className={span}>
                                <EditableImageSlot
                                    testId={`gallery-slot-${i}`}
                                    url={slot}
                                    fallback={defaultUrl}
                                    label={`Foto ${i + 1}`}
                                    aspect="aspect-auto h-full"
                                    onChange={(newUrl) =>
                                        updateSlot("gallery_images", i, newUrl, "")
                                    }
                                    onUpload={(file) => uploadToUrl(file, "gallery")}
                                />
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* Notification banner (top marquee) */}
            <Card className="p-6 mt-6" data-testid="banner-settings">
                <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <p className="font-display font-black text-2xl">Banner de notificaciones</p>
                        <p className="text-sm text-muted-foreground">
                            Texto negro corriendo arriba del logo. Anuncia especiales, teléfono, promociones.
                        </p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <Switch
                            data-testid="banner-enabled"
                            checked={!!form.banner?.enabled}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, banner: { ...f.banner, enabled: v } }))}
                            className="data-[state=checked]:bg-primary"
                        />
                        <span className="text-xs font-bold uppercase tracking-widest">
                            {form.banner?.enabled ? "Activo" : "Oculto"}
                        </span>
                    </label>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                            Texto (Español)
                        </label>
                        <Textarea
                            data-testid="banner-text-es"
                            rows={3}
                            value={form.banner?.text_es || ""}
                            onChange={(e) => setForm((f) => ({ ...f, banner: { ...f.banner, text_es: e.target.value } }))}
                            placeholder="🔥 Tamales disponibles · Chimi $9.75 · Pickup gratis · 📞 (203) 466-6226"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                            Text (English)
                        </label>
                        <Textarea
                            data-testid="banner-text-en"
                            rows={3}
                            value={form.banner?.text_en || ""}
                            onChange={(e) => setForm((f) => ({ ...f, banner: { ...f.banner, text_en: e.target.value } }))}
                            placeholder="🔥 Tamales available now · $9.75 Chimi Special · Free pickup"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                            Velocidad · segundos por vuelta (5–180)
                        </label>
                        <Input
                            data-testid="banner-speed"
                            type="number"
                            min={5}
                            max={180}
                            value={form.banner?.speed_seconds ?? 30}
                            onChange={(e) => setForm((f) => ({ ...f, banner: { ...f.banner, speed_seconds: Number(e.target.value) } }))}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                            Menor = más rápido. 30 segundos es un buen valor.
                        </p>
                    </div>
                </div>
            </Card>

            {/* Discount popup */}
            <Card className="p-6 mt-6" data-testid="popup-settings">
                <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <p className="font-display font-black text-2xl">Popup de descuento</p>
                        <p className="text-sm text-muted-foreground">
                            Ventana modal que aparece a los pocos segundos para captar emails.
                        </p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <Switch
                            data-testid="popup-enabled"
                            checked={!!form.discount_popup?.enabled}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, enabled: v } }))}
                            className="data-[state=checked]:bg-primary"
                        />
                        <span className="text-xs font-bold uppercase tracking-widest">
                            {form.discount_popup?.enabled ? "Activo" : "Oculto"}
                        </span>
                    </label>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <PopupField label="Kicker · Español" testId="popup-kicker-es" value={form.discount_popup?.kicker_es} onChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, kicker_es: v } }))} placeholder="Tiempo limitado" maxLength={40} />
                    <PopupField label="Kicker · English" testId="popup-kicker-en" value={form.discount_popup?.kicker_en} onChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, kicker_en: v } }))} placeholder="Limited time" maxLength={40} />
                    <PopupField label="Título · Español" testId="popup-title-es" value={form.discount_popup?.title_es} onChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, title_es: v } }))} placeholder="Obtén 10% OFF en tu primer pedido" />
                    <PopupField label="Title · English" testId="popup-title-en" value={form.discount_popup?.title_en} onChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, title_en: v } }))} placeholder="Get 10% OFF your first order" />
                    <div className="md:col-span-1">
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                            Cuerpo · Español
                        </label>
                        <Textarea
                            data-testid="popup-body-es"
                            rows={3}
                            value={form.discount_popup?.body_es || ""}
                            onChange={(e) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, body_es: e.target.value } }))}
                            placeholder="Únete a nuestra lista — te enviamos un cupón…"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                            Body · English
                        </label>
                        <Textarea
                            data-testid="popup-body-en"
                            rows={3}
                            value={form.discount_popup?.body_en || ""}
                            onChange={(e) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, body_en: e.target.value } }))}
                            placeholder="Join our list — we'll email you a coupon…"
                        />
                    </div>
                    <PopupField label="Botón · Español" testId="popup-cta-es" value={form.discount_popup?.cta_es} onChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, cta_es: v } }))} placeholder="Enviar mi cupón" />
                    <PopupField label="Button · English" testId="popup-cta-en" value={form.discount_popup?.cta_en} onChange={(v) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, cta_en: v } }))} placeholder="Send me the coupon" />
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                            Aparecer después de (segundos)
                        </label>
                        <Input
                            data-testid="popup-delay"
                            type="number"
                            min={0}
                            max={120}
                            value={form.discount_popup?.delay_seconds ?? 8}
                            onChange={(e) => setForm((f) => ({ ...f, discount_popup: { ...f.discount_popup, delay_seconds: Number(e.target.value) } }))}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                            0 = inmediatamente. Cada visitante solo lo ve una vez (guardado en el navegador).
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
}

function PopupField({ label, testId, value, onChange, placeholder, maxLength }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                {label}
            </label>
            <Input
                data-testid={testId}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
            />
        </div>
    );
}
